import useVisible from '../../hooks/useVisible';
import { Navigation, NavItem as SanityNavItem } from '@root/sanity/sanity.types';
import { useEffect, useState } from 'react';
import { isActiveNavLink } from '@root/src/partials/nav/NavItem';
import TrackedLink from 'components/sanity/TrackedLink';
import { NavTrackingProvider } from './analytics';
import { navItemLink } from './navEvents';

type NavItemsProps = {
  navSection: NonNullable<SanityNavItem['children']>;
  pathname: string;
  setIsVisible: (bool: boolean) => void;
} & ClassNameProp;

const NavItems = ({ navSection, pathname, className, setIsVisible }: NavItemsProps) => {
  return (
    <nav className={`bg-gray-lighter ${className} `}>
      {navSection
        ?.filter((item: SanityNavItem) => !item?.image?.asset)
        .map((item) => (
          <div key={item.title}>
            <TrackedLink
              nav
              navItem={navItemLink(item, navSection)}
              href={`${item.href}`}
              onClick={() => setIsVisible(false)}
              className={`text-sm hover:underline ${
                pathname === item.href || pathname.includes(item.href) ? 'text-gold-darker' : 'text-gray-darker'
              } ${item?.trackerClass ?? ''}`}
              {...(item?.newTab && { target: '_blank', rel: 'noopener noreferrer' })}
            >
              {item.title}
            </TrackedLink>
          </div>
        ))}
    </nav>
  );
};

type NavSubProps = {
  navigation: Navigation;
  pathname: string;
} & ClassNameProp;

const NavSub = ({ navigation, pathname, className, ...props }: NavSubProps) => {
  const { ref, setIsVisible } = useVisible(false);
  const [navSection, setNavSection] = useState<Navigation['items'][number]>();

  useEffect(() => {
    const activeNavSection = navigation?.items?.find(({ href }) => isActiveNavLink(pathname, href));
    setNavSection(activeNavSection);
  }, [pathname]);

  if (!navSection?.children?.length) return null;

  return (
    <div ref={ref} {...props} className={`bg-gray-lighter ${className}`}>
      <NavTrackingProvider item={navItemLink(navSection, navigation.items)}>
        <NavItems
          navSection={navSection?.children}
          pathname={pathname}
          setIsVisible={setIsVisible}
          className={`hidden space-x-16 px-16 py-16 lg:flex lg:pl-[164px]`}
        />
      </NavTrackingProvider>
    </div>
  );
};

export default NavSub;
